import type { ReactNode } from "react";
import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import { SpinningLoader } from "@/components/SpinningLoader";
import { PATHS } from "@/constants/paths";
import AdminTourProvider from "@/providers/AdminTourProvider";

import { AdminNonProtectedRoute } from "./AdminNonProtectedRoute";
import { AdminProtectedRoute } from "./AdminProtectedRoute";
import { RouteErrorBoundary } from "./RouteErrorBoundary";

// NOTE: istanbul ignore on lazy factories skips the import() stub; route modules are
// covered when Playwright navigates to each admin path.

/* istanbul ignore next */
const AdminAuthProvider = lazy(async () => {
  const module = await import("@/providers/AdminAuthProvider");
  return { default: module.AdminAuthProvider };
});

const AdminSignIn = lazy(() => import("@/pages/admin/auth/SignIn"));
const AdminDashboard = lazy(() => import("@/pages/admin/dashboard/Dashboard"));
const AdminUsers = lazy(() => import("@/pages/admin/users/Users"));
const AdminUserDetail = lazy(() => import("@/pages/admin/users/UserDetail"));
const AdminOrganizations = lazy(
  () => import("@/pages/admin/organizations/Organizations"),
);
const AdminOrganizationDetail = lazy(
  () => import("@/pages/admin/organizations/OrganizationDetail"),
);
const AdminProjects = lazy(() => import("@/pages/admin/projects/Projects"));
const AdminModels = lazy(() => import("@/pages/admin/models/Models"));
const AdminModelDetail = lazy(() => import("@/pages/admin/models/ModelDetail"));
const AdminProbes = lazy(() => import("@/pages/admin/probes/Probes"));
const AdminProbeDetail = lazy(() => import("@/pages/admin/probes/ProbeDetail"));
const AdminRoles = lazy(() => import("@/pages/admin/roles/Roles"));
const AdminRoleDetail = lazy(() => import("@/pages/admin/roles/RoleDetail"));
const AdminIntegrations = lazy(
  () => import("@/pages/admin/integrations/Integrations"),
);
const AdminIntegrationDetail = lazy(
  () => import("@/pages/admin/integrations/IntegrationDetail"),
);
const AdminChats = lazy(() => import("@/pages/admin/chats/Chats"));
const AdminChatDetail = lazy(() => import("@/pages/admin/chats/ChatDetail"));
const AdminMeetings = lazy(() => import("@/pages/admin/meetings/Meetings"));
const AdminMeetingDetail = lazy(
  () => import("@/pages/admin/meetings/MeetingDetail"),
);
const AdminKnowledge = lazy(() => import("@/pages/admin/knowledge/Knowledge"));
const AdminKnowledgeDetail = lazy(
  () => import("@/pages/admin/knowledge/KnowledgeDetail"),
);
const AdminExternalApiKeys = lazy(
  () => import("@/pages/admin/external-api-keys/ExternalApiKeys"),
);
const AdminExternalApiKeyDetail = lazy(
  () => import("@/pages/admin/external-api-keys/ExternalApiKeyDetail"),
);
const AdminExternalApiLimits = lazy(
  () => import("@/pages/admin/external-api-limits/ExternalApiLimits"),
);
const AdminAuditLogs = lazy(() => import("@/pages/admin/audit-logs/AuditLogs"));
const AdminAccounts = lazy(() => import("@/pages/admin/accounts/Accounts"));
const AdminSessions = lazy(() => import("@/pages/admin/sessions/Sessions"));
const AdminUserSessions = lazy(
  () => import("@/pages/admin/user-sessions/UserSessions"),
);
const AdminSettings = lazy(() => import("@/pages/admin/settings/Settings"));
const AdminAutomations = lazy(
  () => import("@/pages/admin/automations/Automations"),
);
const AdminCronJobDetail = lazy(
  () => import("@/pages/admin/cron-jobs/CronJobDetail"),
);

/* istanbul ignore next */
const AdminActivityLog = lazy(
  () => import("@/pages/admin/activity-log/ActivityLog"),
);
/* istanbul ignore next */
const AdminSsoDomains = lazy(
  () => import("@/pages/admin/sso-domains/AdminSsoDomains"),
);
const AdminAppearance = lazy(
  () => import("@/pages/admin/appearance/AdminAppearance"),
);
/* istanbul ignore next */
const AdminAssistants = lazy(
  () => import("@/pages/admin/assistants/Assistants"),
);
/* istanbul ignore next */
const AdminAssistantDetail = lazy(
  () => import("@/pages/admin/assistants/AssistantDetail"),
);
/* istanbul ignore next */
const AdminProjectDetail = lazy(
  () => import("@/pages/admin/projects/ProjectDetail"),
);

const AdminLayout = lazy(() => import("@/pages/admin/layout/Layout"));

interface IAdminSuspenseProps {
  children: ReactNode;
}

// NOTE: NSAI-5414 — the admin tree had no error boundary at all (the regular
// app gets one per route from `RouteWrapper`), so one render-time throw on a
// page — e.g. an admin transcript rendering an attachment whose stored MIME type
// broke the file-icon lookup — unmounted the whole admin SPA to a blank page,
// and reloading the same URL crashed it again. Every level that suspends now
// also catches: a page throw keeps the admin layout and sidebar mounted and
// shows the recoverable fallback in the content area instead.
const AdminSuspense = ({ children }: IAdminSuspenseProps): ReactNode => (
  <RouteErrorBoundary>
    <Suspense
      fallback={
        // NOTE: `w-full`, never `w-screen`. Every admin tab is a lazy route, so
        // this fallback is what a tab switch shows. `100vw` counts a classic
        // vertical scrollbar's gutter as page width, which pushes the document
        // sideways and leaves the spinner off-centre by half the gutter — the
        // same trap CenterCardLayout documents.
        <div className="flex h-screen w-full items-center justify-center">
          <SpinningLoader />
        </div>
      }
    >
      {children}
    </Suspense>
  </RouteErrorBoundary>
);

// NOTE: The in-layout fallback fills the content area, not the viewport. These
// routes render inside AdminLayout's <Outlet />, so the `h-screen` fallback
// above would be taller than the box it sits in: it blanked the page, then
// handed over to the page's own list loader — two spinners in two places for
// one navigation. Filling the same box keeps it to one, in one place.
const AdminPageSuspense = ({ children }: IAdminSuspenseProps): ReactNode => (
  <RouteErrorBoundary>
    <Suspense
      fallback={
        <div className="flex h-full w-full items-center justify-center">
          <SpinningLoader />
        </div>
      }
    >
      {children}
    </Suspense>
  </RouteErrorBoundary>
);

export const AdminRoutes = (): ReactNode => {
  return (
    <AdminSuspense>
      <AdminAuthProvider>
        <Routes>
          {/* NOTE: Non-protected admin auth routes */}
          <Route
            element={
              <AdminNonProtectedRoute>
                <Outlet />
              </AdminNonProtectedRoute>
            }
          >
            <Route
              path="auth/sign-in"
              element={
                <AdminSuspense>
                  <AdminSignIn />
                </AdminSuspense>
              }
            />
          </Route>

          {/* NOTE: Protected admin routes with layout */}
          <Route
            element={
              <AdminProtectedRoute>
                <AdminTourProvider>
                  <AdminSuspense>
                    <AdminLayout />
                  </AdminSuspense>
                </AdminTourProvider>
              </AdminProtectedRoute>
            }
          >
            <Route
              index
              element={<Navigate to={PATHS.ADMIN.DASHBOARD} replace />}
            />
            <Route
              path="dashboard"
              element={
                <AdminPageSuspense>
                  <AdminDashboard />
                </AdminPageSuspense>
              }
            />
            <Route
              path="users"
              element={
                <AdminPageSuspense>
                  <AdminUsers />
                </AdminPageSuspense>
              }
            />
            <Route
              path="users/:uuid"
              element={
                <AdminPageSuspense>
                  <AdminUserDetail />
                </AdminPageSuspense>
              }
            />
            <Route
              path="organizations"
              element={
                <AdminPageSuspense>
                  <AdminOrganizations />
                </AdminPageSuspense>
              }
            />
            <Route
              path="organizations/:uuid"
              element={
                <AdminPageSuspense>
                  <AdminOrganizationDetail />
                </AdminPageSuspense>
              }
            />
            <Route
              path="projects"
              element={
                <AdminPageSuspense>
                  <AdminProjects />
                </AdminPageSuspense>
              }
            />
            <Route
              path="projects/:uuid"
              element={
                <AdminPageSuspense>
                  <AdminProjectDetail />
                </AdminPageSuspense>
              }
            />
            <Route
              path="models"
              element={
                <AdminPageSuspense>
                  <AdminModels />
                </AdminPageSuspense>
              }
            />
            <Route
              path="models/:uuid"
              element={
                <AdminPageSuspense>
                  <AdminModelDetail />
                </AdminPageSuspense>
              }
            />
            <Route
              path="assistants"
              element={
                <AdminPageSuspense>
                  <AdminAssistants />
                </AdminPageSuspense>
              }
            />
            <Route
              path="assistants/:uuid"
              element={
                <AdminPageSuspense>
                  <AdminAssistantDetail />
                </AdminPageSuspense>
              }
            />
            <Route
              path="probes"
              element={
                <AdminPageSuspense>
                  <AdminProbes />
                </AdminPageSuspense>
              }
            />
            <Route
              path="probes/:uuid"
              element={
                <AdminPageSuspense>
                  <AdminProbeDetail />
                </AdminPageSuspense>
              }
            />
            <Route
              path="roles"
              element={
                <AdminPageSuspense>
                  <AdminRoles />
                </AdminPageSuspense>
              }
            />
            <Route
              path="roles/:uuid"
              element={
                <AdminPageSuspense>
                  <AdminRoleDetail />
                </AdminPageSuspense>
              }
            />
            <Route
              path="integrations"
              element={
                <AdminPageSuspense>
                  <AdminIntegrations />
                </AdminPageSuspense>
              }
            />
            <Route
              path="integrations/:uuid"
              element={
                <AdminPageSuspense>
                  <AdminIntegrationDetail />
                </AdminPageSuspense>
              }
            />
            <Route
              path="chats"
              element={
                <AdminPageSuspense>
                  <AdminChats />
                </AdminPageSuspense>
              }
            />
            <Route
              path="chats/:uuid"
              element={
                <AdminPageSuspense>
                  <AdminChatDetail />
                </AdminPageSuspense>
              }
            />
            <Route
              path="meetings"
              element={
                <AdminPageSuspense>
                  <AdminMeetings />
                </AdminPageSuspense>
              }
            />
            <Route
              path="meetings/:uuid"
              element={
                <AdminPageSuspense>
                  <AdminMeetingDetail />
                </AdminPageSuspense>
              }
            />
            <Route
              path="appearance"
              element={
                <AdminPageSuspense>
                  <AdminAppearance />
                </AdminPageSuspense>
              }
            />
            <Route
              path="sso-domains"
              element={
                <AdminSuspense>
                  <AdminSsoDomains />
                </AdminSuspense>
              }
            />
            <Route
              path="knowledge"
              element={
                <AdminPageSuspense>
                  <AdminKnowledge />
                </AdminPageSuspense>
              }
            />
            <Route
              path="knowledge/:uuid"
              element={
                <AdminPageSuspense>
                  <AdminKnowledgeDetail />
                </AdminPageSuspense>
              }
            />
            <Route
              path="automations"
              element={
                <AdminPageSuspense>
                  <AdminAutomations />
                </AdminPageSuspense>
              }
            />
            <Route
              path="automations/cron-jobs"
              element={<Navigate to={PATHS.ADMIN.AUTOMATIONS} replace />}
            />
            <Route
              path="automations/cron-jobs/:uuid"
              element={
                <AdminPageSuspense>
                  <AdminCronJobDetail />
                </AdminPageSuspense>
              }
            />
            <Route
              path="automations/cron-runs"
              element={<Navigate to={PATHS.ADMIN.AUTOMATIONS} replace />}
            />
            <Route
              path="external-api-keys"
              element={
                <AdminPageSuspense>
                  <AdminExternalApiKeys />
                </AdminPageSuspense>
              }
            />
            <Route
              path="external-api-keys/:uuid"
              element={
                <AdminPageSuspense>
                  <AdminExternalApiKeyDetail />
                </AdminPageSuspense>
              }
            />
            <Route
              path="external-api-limits"
              element={
                <AdminSuspense>
                  <AdminExternalApiLimits />
                </AdminSuspense>
              }
            />
            <Route
              path="audit-logs"
              element={
                <AdminPageSuspense>
                  <AdminAuditLogs />
                </AdminPageSuspense>
              }
            />
            <Route
              path="accounts"
              element={
                <AdminPageSuspense>
                  <AdminAccounts />
                </AdminPageSuspense>
              }
            />
            <Route
              path="sessions"
              element={
                <AdminPageSuspense>
                  <AdminSessions />
                </AdminPageSuspense>
              }
            />
            <Route
              path="user-sessions"
              element={
                <AdminPageSuspense>
                  <AdminUserSessions />
                </AdminPageSuspense>
              }
            />
            <Route
              path="activity-log"
              element={
                <AdminPageSuspense>
                  <AdminActivityLog />
                </AdminPageSuspense>
              }
            />
            <Route
              path="settings"
              element={
                <AdminPageSuspense>
                  <AdminSettings />
                </AdminPageSuspense>
              }
            />
          </Route>

          {/* NOTE: Fallback - redirect to sign in. NSAI-4174 — absolute on
              purpose: under v7's relative splat paths a relative "auth/sign-in"
              resolves against the whole unmatched URL (/admin/x →
              /admin/x/auth/sign-in), which this same catch-all matches again,
              looping forever. */}
          <Route
            path="*"
            element={<Navigate to={PATHS.ADMIN.AUTH.SIGN_IN} replace />}
          />
        </Routes>
      </AdminAuthProvider>
    </AdminSuspense>
  );
};
